// The seller's invoice on an order paid on delivery (no Stripe document): whether the buyer asked
// for one, the PDF once it is there (its name opens it in the viewer), and its upload: a PDF dropped
// on the drop area, or picked by a click. Replacing and removing it is an admin's (the API says so:
// canUpload, canRemove). Shown when the project's orders API has the invoice methods (api.invoice,
// api.invoiceFile, api.uploadInvoice, api.removeInvoice) and the order is paid on delivery.
import React, { useEffect, useMemo, useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import PDFViewer from "aldoo-ra/PDFViewer"
import DropZone from "aldoo-ra/DropZone"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import { Z } from "aldoo-ra/ZLayers"
import useIsMobile from "aldoo-ra/Hooks/use-is-mobile"
import { ArrowPathIcon, ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { Chip, dateTime, errorMessage } from "./ui"

const bytesOf = (file) => Uint8Array.from(atob(file), (c) => c.charCodeAt(0))

function savePdf({ file, filename, contentType }) {
  const bytes = bytesOf(file)
  const url = URL.createObjectURL(new Blob([bytes], { type: contentType || "application/pdf" }))
  const link = document.createElement("a")
  link.href = url
  link.download = filename || "invoice.pdf"
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30000)
}

export default function InvoicePanel({ order, api, t, locale, onChanged }) {
  const applies = order?.paymentMethod?.type === "cash_on_delivery" && api?.invoice && api?.uploadInvoice
  const [info, setInfo] = useState(null)
  const [busy, setBusy] = useState(false)
  // the PDF as the API answered it (fetched once), and whether the viewer is open
  const [file, setFile] = useState(null)
  const [viewing, setViewing] = useState(false)
  const bytes = useMemo(() => (file?.file ? bytesOf(file.file) : null), [file])
  const viewerTexts = useMemo(() => ({ loading: t`Loading…`, error: t`Could not load the invoice`, zoomOut: t`Zoom out`, zoomIn: t`Zoom in`, fitWidth: t`Fit width`, prevPage: t`Previous page`, nextPage: t`Next page`, download: t`Download`, pages: t`Pages`, showPages: t`Show the pages`, hidePages: t`Hide the pages`, pageN: t`Page %s` }), [t])
  const input = useRef(null)
  // the viewer: the whole screen on a phone
  const mobile = useIsMobile()
  const id = order?._id
  const completed = order?.orderStatus === "completed"

  useEffect(() => {
    if (!applies) return
    let live = true
    api.invoice(id).then((res) => live && setInfo(res?.error ? null : res))
    return () => {
      live = false
    }
  }, [applies, id])

  if (!applies || !info) return null
  const fail = (res) => showNotification({ message: t(errorMessage(res) || "Something went wrong"), className: "bg-admin_error text-white" })

  // the invoice's PDF, fetched once (again after a replacement)
  const fetchFile = async () => {
    if (file) return file
    setBusy(true)
    const res = await api.invoiceFile(id).catch((e) => ({ error: e.message }))
    setBusy(false)
    if (!res?.file) {
      fail(res)
      return null
    }
    setFile(res)
    return res
  }
  const view = async () => (await fetchFile()) && setViewing(true)
  const download = async () => {
    const res = await fetchFile()
    if (res) savePdf(res)
  }

  const upload = async (file) => {
    if (!file) return
    setBusy(true)
    const res = await api.uploadInvoice(id, file).catch((e) => ({ error: e.message }))
    setBusy(false)
    if (input.current) input.current.value = ""
    if (!res?.result) return fail(res)
    showNotification({ message: t`Invoice uploaded`, className: "bg-admin_success text-white" })
    setFile(null)
    setInfo({ ...info, invoice: res.invoice, canUpload: false })
    // the list's "awaiting invoice" goes with it; an admin may replace it again (asked anew)
    api.invoice(id).then((fresh) => !fresh?.error && setInfo(fresh))
    onChanged?.()
  }

  // an admin takes it off (asked first): the order awaits its invoice again
  const remove = async () => {
    if (!api.removeInvoice) return
    const ok = await showPromptDialogAsync({ variant: "admin", title: t`Remove the invoice?`, message: t("%s is taken off the order: the buyer no longer sees it, and a new one can be uploaded.", info.invoice?.name || ""), confirmText: t`Remove`, cancelText: t`Cancel` })
    if (!ok) return
    setBusy(true)
    const res = await api.removeInvoice(id).catch((e) => ({ error: e.message }))
    setBusy(false)
    if (!res?.result) return fail(res)
    showNotification({ message: t`Invoice removed`, className: "bg-admin_success text-white" })
    setFile(null)
    api.invoice(id).then((fresh) => !fresh?.error && setInfo(fresh))
    onChanged?.()
  }

  return (
    <div className="col-span-2 print:hidden" data-testid="order-invoice-panel">
      <div className="text-black/50 uppercase text-[11px] tracking-wide mb-1">{t`Invoice`}</div>
      {info.invoice ? (
        <div className="flex items-center justify-between gap-4">
          {/* an admin may take it off (asked first); its name opens it in the viewer (whose toolbar
              saves it); when it was uploaded under it */}
          <div className="flex items-start gap-2 min-w-0">
          {info.canRemove && api.removeInvoice && (
            <button type="button" onClick={remove} disabled={busy} title={t`Remove the invoice`} aria-label={t`Remove the invoice`} className="mt-0.5 w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-black/50 hover:text-admin_error hover:bg-admin_error/10 disabled:opacity-60" data-testid="order-invoice-remove">
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
          <div className="flex flex-col min-w-0 text-sm">
            <button type="button" onClick={view} disabled={busy} className="font-medium text-admin_primary hover:underline disabled:opacity-60 text-left truncate" data-testid="order-invoice-view">
              {info.invoice.name}
            </button>
            <span className="text-black/50">{t("Uploaded %s", dateTime(info.invoice.uploadedAt, locale))}</span>
          </div>
          </div>
          {/* an admin may replace it */}
          {info.canUpload && (
            <Button variant="admin_secondary" disabled={busy} onClick={() => input.current?.click()} className="shrink-0" data-testid="order-invoice-upload">
              <span className="inline-flex items-center gap-1.5"><ArrowPathIcon className="w-4 h-4" />{t`Replace`}</span>
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* the invoice goes with a completed order (delivered and paid); the drop area takes one at any state */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {info.requested && <Chip className="bg-admin_warning/20 text-admin_text">{t`awaiting invoice`}</Chip>}
            <span className="text-black/60">
              {info.requested
                ? completed
                  ? t`The buyer asked for an invoice: the order is completed, upload it now.`
                  : t`Once the order is paid, attach your invoice for the buyer here.`
                : completed
                  ? t`No invoice asked for. You can still add one.`
                  : t`No invoice asked for. You can add one once the order is completed, delivered and paid.`}
            </span>
          </div>
          {/* the PDF dropped here, or picked by a click */}
          {info.canUpload && (
            <DropZone
              accept={{ "application/pdf": [".pdf"] }}
              maxFiles={1}
              onDrop={(accepted, rejected) => (accepted?.[0] ? upload(accepted[0]) : rejected?.length && fail({ message: t`The invoice must be a PDF` }))}
              onRender={({ isDragging, handleClick, handleDragEnter, handleDragOver, handleDragLeave, handleDrop }) => (
                <div
                  onClick={busy ? undefined : handleClick}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed transition py-10 ${busy ? "opacity-60 cursor-wait" : "cursor-pointer"} ${isDragging ? "border-admin_primary bg-admin_primary/10" : "border-admin_bordered hover:border-admin_primary"}`}
                  data-testid="order-invoice-drop"
                >
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition ${isDragging ? "bg-admin_primary/20" : "bg-admin_neutral_1"}`}>
                    <ArrowUpTrayIcon className={`w-6 h-6 transition ${isDragging ? "text-admin_primary" : "text-black/50"}`} />
                  </div>
                  <div className="text-center text-sm">
                    <div className="font-semibold">{busy ? t`Uploading…` : t`Drop the invoice PDF here, or click to choose it`}</div>
                    <div className="text-black/50 mt-1">{t`PDF, up to 15 MB`}</div>
                  </div>
                </div>
              )}
            />
          )}
        </div>
      )}
      <input ref={input} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="order-invoice-input" />
      {viewing && bytes && (
        // opened from inside the order's dialog: above every dialog (ZLayers modalTop), under the system dialogs and toasts
        <Modal
          isOpen
          onClose={() => setViewing(false)}
          variant={mobile ? "admin_fullscreen" : "admin"}
          closeOnEscape
          zIndex={Z.modalTop}
          title={info.invoice?.name || t`Invoice`}
          styleOverrides={mobile ? { content: "px-0 pb-0 h-full min-h-0" } : { container: "w-[min(96vw,980px)] bg-admin_paper rounded-lg border border-admin_bordered shadow-lg overflow-hidden" }}
        >
          <div className={mobile ? "" : "p-3"} data-testid="order-invoice-viewer">
            <PDFViewer src={bytes} height={mobile ? "calc(100dvh - 64px)" : "75vh"} onDownload={download} texts={viewerTexts} />
          </div>
        </Modal>
      )}
    </div>
  )
}
